import React from 'react';
import { InView } from 'react-intersection-observer';
import { useViewEvent } from '@stitch-fix/event-reporter';
import Carousel, { CarouselProps } from '../Carousel';
import CategoryCard, { CategoryCardProps } from '../CategoryCard';
import CategorySetModuleHeader from '../CategoryCard/CategorySetModuleHeader';
import { getResponsiveValue, ResponsiveValue } from '../_internal/styling';
import useScreenSize from '../_internal/useScreenSize';
import { ModuleHeaderProps } from '../ModuleHeader';
import Placeholder from '../Placeholder';
import Stack from '../Stack';
import EndCard, { EndCardProps } from './EndCard';

export type CarouselCategoryCard = Omit<
  CategoryCardProps,
  'variant' | 'tagline'
>;

export interface CategoriesCarouselProps
  extends Pick<CarouselProps, 'ariaLabel' | 'gutterOverflow'> {
  /**
   * An array of card objects that will be displayed in the categories carousel
   *
   * CarouselCategoryCard =  Omit<CategoryCardProps, 'variant' | 'tagline'>
   */
  categoryCards?: CarouselCategoryCard[];

  /**
   * A category set ID representing this collection of categories. E.g. 'departments', 'fresh_finds', 'brands'.
   */
  categorySetId: string;

  /**
   * The end card is optional. If an end card is passed in, it will be the last card in the category carousel.
   *
   * CarouselEndCard = Pick<CategoryCardProps, 'cta' | 'linkAs'> & {image: Exclude<CategoryCardProps['image'], JSX.Element>;
   */
  endCard?: EndCardProps['endCard'];

  /**
   * Displays a loading state when true.
   * @default false
   */
  isLoading?: boolean;

  /**
   * When provided, is passed as the props to render a `<ModuleHeader`> above the carousel.
   *
   * By using this prop, the header link will be instrumented with a `view_all_link` Product
   * Tracking Event (PTE).
   */
  moduleHeader?: ModuleHeaderProps;

  /**
   * Callback to execute when a card category is clicked
   */
  onCategoryClick?: (categoryId: string) => void;

  /**
   * Callback to execute when a category card is viewed. Only triggered once.
   */
  onCategoryView?: (categoryId: string) => void;

  /**
   * Callback to execute when an end card is clicked
   */
  onEndCardClick?: () => void;

  /**
   * Callback to execute when an end card is viewed. Only triggered once.
   */
  onEndCardView?: () => void;

  /**
   * This defines the styling for the cards.
   * WIP: Currently only `editorial` and `icon` are supported with proper styling.
   * The `logo` variant can be added once styling has been applied to support the variant.
   * 'editorial' cards have 4:5 images.
   * 'icon' cards have 1:1 images.
   * 'logo' cards have 3:2 images.
   */
  variant: 'editorial' | 'icon';
}

const DEFAULT_SLIDE_WIDTH = 160;

// Some image variants have different heights and this lookup table will manage
// the offset placement of the controls for each variant.
const controlsOffsetByVariant: Record<CategoryCardProps['variant'], number> = {
  editorial: 4 / 5,
  icon: 1,
  logo: 1,
  text: 1,
};

// Each card variant has different responsive widths
const slideWidthByVariant: Record<
  CategoryCardProps['variant'],
  ResponsiveValue<number>
> = {
  editorial: {
    sm: 192,
    md: 304,
  },
  icon: {
    sm: 104,
    md: DEFAULT_SLIDE_WIDTH,
  },
  logo: 112,
  text: 112,
};

/**
 * The CategoriesCarousel is a carousel populated with CategoryCards specifically used to show categories such as fresh finds, departments, etc.
 *
 * Exposed via a nested import: `import CategoriesCarousel from '@stitch-fix/mode-react/CategoriesCarousel';`
 *
 * This component is pre-instrumented with Product Tracking Events (PTEs).
 *
 * **WIP: Currently only the icon version of this is being used in prod. While this component could be used for text/editorial/logo, additional changes may need to be made.**
 */
const CategoriesCarousel = ({
  ariaLabel,
  categoryCards = [],
  categorySetId,
  endCard,
  gutterOverflow,
  isLoading = false,
  moduleHeader,
  onCategoryClick,
  onCategoryView,
  onEndCardClick,
  onEndCardView,
  variant,
}: CategoriesCarouselProps) => {
  const { ref, EventProvider } = useViewEvent({
    schema: 'category_set_view',
    category_set_id: categorySetId,
    name: 'carousel',
  });

  const screenSize = useScreenSize();

  const slideWidth = getResponsiveValue(
    screenSize,
    slideWidthByVariant[variant],
    DEFAULT_SLIDE_WIDTH,
  );
  // Setting controls to be 50% from top of the image vs entire slide so that controls
  // look centered in the images which have a stronger visual weight.
  // width / (image aspect ratio) / 2
  const controlsOffsetTop = `${
    slideWidth / controlsOffsetByVariant[variant] / 2
  }px`;

  if (isLoading) {
    return (
      <Carousel
        ariaLabel={ariaLabel}
        gutterOverflow={gutterOverflow}
        slideWidth="160px"
        withoutControls
        data-testid="loading-carousel"
      >
        {Array.from({ length: 8 }).map((_, index) => (
          <Placeholder
            // eslint-disable-next-line react/no-array-index-key
            key={`slide-${index}`}
            data-testid="loading-placeholder"
            pt="100%"
            variant="rect"
          />
        ))}
      </Carousel>
    );
  }

  // if the component isn't loading, and we don't have any category cards, do not render the CategoriesCarousel
  if (!isLoading && categoryCards.length === 0) {
    return null;
  }

  return (
    <EventProvider>
      <Stack ref={ref} spacing={1}>
        {moduleHeader && (
          <CategorySetModuleHeader
            categorySetId={categorySetId}
            moduleHeader={moduleHeader}
          />
        )}
        <Carousel
          ariaLabel={ariaLabel}
          controlsOffsetTop={controlsOffsetTop}
          gutterOverflow={gutterOverflow}
          slideWidth={`${slideWidth}px`}
        >
          {categoryCards.map(({ categoryId, ...card }) => (
            <InView
              key={categoryId}
              data-testid="category-card-carousel-observer-wrapper"
              onChange={inView => inView && onCategoryView?.(categoryId)}
              triggerOnce
            >
              <CategoryCard
                {...card}
                categoryId={categoryId}
                data-testid={`category-card-${categoryId}`}
                onClick={() => onCategoryClick?.(categoryId)}
                variant={variant}
              />
            </InView>
          ))}
          {endCard && (
            <EndCard
              categorySetId={categorySetId}
              endCard={endCard}
              onClick={onEndCardClick}
              onView={onEndCardView}
              variant={variant}
            />
          )}
        </Carousel>
      </Stack>
    </EventProvider>
  );
};

export default CategoriesCarousel;
